/**
 * Composant d'entrée de numéro de téléphone avec drapeaux SVG haute qualité
 * Support des territoires Outre-Mer et France métropolitaine
 */
import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Phone } from 'lucide-react';

// Drapeaux SVG pour chaque territoire
const FLAGS_SVG = {
  GP: () => (
    <svg viewBox="0 0 640 480" className="w-6 h-4 rounded-sm shadow-sm">
      <path fill="#002654" d="M0 0h640v480H0z"/>
      <path fill="#fff" d="M213.3 0h213.4v480H213.3z"/>
      <path fill="#ce1126" d="M426.7 0H640v480H426.7z"/>
      <circle fill="#fcd116" cx="320" cy="240" r="60"/>
    </svg>
  ),
  MQ: () => (
    <svg viewBox="0 0 640 480" className="w-6 h-4 rounded-sm shadow-sm">
      <path fill="#002654" d="M0 0h640v480H0z"/>
      <path fill="#fff" d="M213.3 0h213.4v480H213.3z"/>
      <path fill="#ce1126" d="M426.7 0H640v480H426.7z"/>
      <path fill="#fff" d="M280 200h80v80h-80z"/>
    </svg>
  ),
  GF: () => (
    <svg viewBox="0 0 640 480" className="w-6 h-4 rounded-sm shadow-sm">
      <path fill="#078930" d="M0 0h640v480H0z"/>
      <path fill="#fcdd09" d="M0 0h640L0 480z"/>
      <path fill="#da121a" d="M252 216.5l63.5 195.5 166-120.5H189l166 120.5z"/>
    </svg>
  ),
  RE: () => (
    <svg viewBox="0 0 640 480" className="w-6 h-4 rounded-sm shadow-sm">
      <path fill="#002654" d="M0 0h640v480H0z"/>
      <path fill="#fff" d="M213.3 0h213.4v480H213.3z"/>
      <path fill="#ce1126" d="M426.7 0H640v480H426.7z"/>
      <path fill="#009e60" d="M290 220h60v60h-60z"/>
    </svg>
  ),
  YT: () => (
    <svg viewBox="0 0 640 480" className="w-6 h-4 rounded-sm shadow-sm">
      <path fill="#002654" d="M0 0h640v480H0z"/>
      <path fill="#fff" d="M213.3 0h213.4v480H213.3z"/>
      <path fill="#ce1126" d="M426.7 0H640v480H426.7z"/>
      <circle fill="#fcd116" cx="320" cy="240" r="40"/>
    </svg>
  ),
  NC: () => (
    <svg viewBox="0 0 640 480" className="w-6 h-4 rounded-sm shadow-sm">
      <path fill="#009543" d="M0 0h640v160H0z"/>
      <path fill="#ed4135" d="M0 160h640v160H0z"/>
      <path fill="#0035ad" d="M0 320h640v160H0z"/>
      <circle fill="#fcd116" cx="160" cy="240" r="80"/>
    </svg>
  ),
  PF: () => (
    <svg viewBox="0 0 640 480" className="w-6 h-4 rounded-sm shadow-sm">
      <path fill="#ce1126" d="M0 0h640v120H0z"/>
      <path fill="#fff" d="M0 120h640v240H0z"/>
      <path fill="#ce1126" d="M0 360h640v120H0z"/>
      <circle fill="#003399" cx="320" cy="240" r="70"/>
      <circle fill="#fcd116" cx="320" cy="240" r="40"/>
    </svg>
  ),
  SX: () => (
    <svg viewBox="0 0 640 480" className="w-6 h-4 rounded-sm shadow-sm">
      <path fill="#002654" d="M0 0h640v480H0z"/>
      <path fill="#fff" d="M213.3 0h213.4v480H213.3z"/>
      <path fill="#ce1126" d="M426.7 0H640v480H426.7z"/>
      <path fill="#fcd116" d="M300 210h40v60h-40z"/>
    </svg>
  ),
  BL: () => (
    <svg viewBox="0 0 640 480" className="w-6 h-4 rounded-sm shadow-sm">
      <path fill="#002654" d="M0 0h640v480H0z"/>
      <path fill="#fff" d="M213.3 0h213.4v480H213.3z"/>
      <path fill="#ce1126" d="M426.7 0H640v480H426.7z"/>
      <path fill="#fff" d="M305 200l15 45h47l-38 28 15 45-39-28-39 28 15-45-38-28h47z"/>
    </svg>
  ),
  FR: () => (
    <svg viewBox="0 0 640 480" className="w-6 h-4 rounded-sm shadow-sm">
      <path fill="#002654" d="M0 0h213.3v480H0z"/>
      <path fill="#fff" d="M213.3 0h213.4v480H213.3z"/>
      <path fill="#ce1126" d="M426.7 0H640v480H426.7z"/>
    </svg>
  )
};

// Données des pays/territoires avec drapeaux et indicatifs
const PHONE_COUNTRIES = [
  { code: 'GP', name: 'Guadeloupe', dial: '+590', prefix: ['0690', '0590'], placeholder: '0690 XX XX XX', region: 'antilles' },
  { code: 'MQ', name: 'Martinique', dial: '+596', prefix: ['0696', '0596'], placeholder: '0696 XX XX XX', region: 'antilles' },
  { code: 'SX', name: 'Saint-Martin', dial: '+590', prefix: ['0690'], placeholder: '0690 XX XX XX', region: 'antilles' },
  { code: 'BL', name: 'Saint-Barthélemy', dial: '+590', prefix: ['0690'], placeholder: '0690 XX XX XX', region: 'antilles' },
  { code: 'GF', name: 'Guyane', dial: '+594', prefix: ['0694', '0594'], placeholder: '0694 XX XX XX', region: 'guyane' },
  { code: 'RE', name: 'La Réunion', dial: '+262', prefix: ['0692', '0262'], placeholder: '0692 XX XX XX', region: 'ocean_indien' },
  { code: 'YT', name: 'Mayotte', dial: '+262', prefix: ['0639', '0269'], placeholder: '0639 XX XX XX', region: 'ocean_indien' },
  { code: 'NC', name: 'Nouvelle-Calédonie', dial: '+687', prefix: [''], placeholder: 'XX XX XX', region: 'pacifique' },
  { code: 'PF', name: 'Polynésie française', dial: '+689', prefix: [''], placeholder: 'XX XX XX XX', region: 'pacifique' },
  { code: 'FR', name: 'France métropolitaine', dial: '+33', prefix: ['06', '07'], placeholder: '06 XX XX XX XX', region: 'metropole' }
];

// Groupes de régions
const REGIONS = {
  antilles: { name: '🌴 Antilles', color: 'bg-emerald-500/20 text-emerald-400' },
  guyane: { name: '🌿 Guyane', color: 'bg-green-500/20 text-green-400' },
  ocean_indien: { name: '🌊 Océan Indien', color: 'bg-blue-500/20 text-blue-400' },
  pacifique: { name: '🏝️ Pacifique', color: 'bg-cyan-500/20 text-cyan-400' },
  metropole: { name: '🇫🇷 Métropole', color: 'bg-indigo-500/20 text-indigo-400' }
};

// Détecter le pays en fonction du numéro
const detectCountryFromNumber = (number) => {
  const cleanNumber = number.replace(/\s/g, '').replace(/\./g, '').replace(/-/g, '');
  
  for (const country of PHONE_COUNTRIES) {
    for (const prefix of country.prefix) {
      if (prefix && cleanNumber.startsWith(prefix)) {
        return country;
      }
    }
    if (cleanNumber.startsWith(country.dial.replace('+', ''))) {
      return country;
    }
  }
  
  return PHONE_COUNTRIES[0];
};

// Composant Flag SVG
const FlagSVG = ({ code }) => {
  const FlagComponent = FLAGS_SVG[code];
  return FlagComponent ? <FlagComponent /> : <div className="w-6 h-4 bg-gray-300 rounded-sm" />;
};

export default function PhoneInputWithFlagSVG({ 
  value = '', 
  onChange, 
  placeholder,
  className = '',
  label,
  required = false,
  defaultCountry = 'GP',
  territory = null
}) {
  // Mapping territoire -> code pays
  const getTerritoryCode = (terr) => {
    const territoryToCode = {
      'Guadeloupe': 'GP',
      'Martinique': 'MQ',
      'Guyane': 'GF',
      'Guyane française': 'GF',
      'La Réunion': 'RE',
      'Réunion': 'RE',
      'Mayotte': 'YT',
      'Nouvelle-Calédonie': 'NC',
      'Polynésie française': 'PF',
      'Polynésie': 'PF',
      'Saint-Pierre-et-Miquelon': 'PM',
      'Wallis-et-Futuna': 'WF',
      'Saint-Barthélemy': 'BL',
      'Saint-Martin': 'SX',
      'France': 'FR',
      'France métropolitaine': 'FR'
    };
    return territoryToCode[terr] || null;
  };

  const getInitialCountry = () => {
    if (territory) {
      const code = getTerritoryCode(territory);
      if (code) {
        return PHONE_COUNTRIES.find(c => c.code === code) || PHONE_COUNTRIES[0];
      }
    }
    return PHONE_COUNTRIES.find(c => c.code === defaultCountry) || PHONE_COUNTRIES[0];
  };

  const [selectedCountry, setSelectedCountry] = useState(getInitialCountry);
  const [isOpen, setIsOpen] = useState(false);
  const [_userSelectedCountry, setUserSelectedCountry] = useState(false); // Track if user manually selected
  const dropdownRef = useRef(null);
  const _previousValueRef = useRef(value);
  const initialLoadDone = useRef(false);

  // NE PAS faire d'auto-détection qui écrase la sélection utilisateur
  // L'utilisateur a le contrôle total sur l'indicatif
  
  // Mettre à jour seulement à l'initialisation si le territoire est spécifié
  useEffect(() => {
    // Ne faire qu'une seule fois au chargement
    if (initialLoadDone.current) return;
    initialLoadDone.current = true;
    
    // Si l'utilisateur a déjà une valeur, ne pas changer
    if (value && value.replace(/\s/g, '').length >= 4) return;
    
    if (territory) {
      const code = getTerritoryCode(territory);
      if (code) {
        const newCountry = PHONE_COUNTRIES.find(c => c.code === code);
        if (newCountry) {
          setSelectedCountry(newCountry);
        }
      }
    }
  }, []); // Ne s'exécute qu'une seule fois au montage

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setUserSelectedCountry(true); // Marquer que l'utilisateur a choisi manuellement
    setIsOpen(false);
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange && onChange(newValue);
    // NE PLUS faire de détection automatique qui écrase la sélection
    // L'utilisateur garde le contrôle total de l'indicatif
  };

  // Grouper les pays par région
  const groupedCountries = Object.entries(REGIONS).map(([key, region]) => ({
    ...region,
    key,
    countries: PHONE_COUNTRIES.filter(c => c.region === key)
  }));

  return (
    <div className={`relative ${className}`} data-testid="phone-input-flag">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      
      <div className="flex items-center gap-0 border-2 rounded-xl focus-within:ring-2 focus-within:ring-amber-500/50 focus-within:border-amber-500 bg-white overflow-hidden transition-all duration-200 shadow-sm hover:shadow-md">
        {/* Sélecteur de pays avec drapeau SVG - TOUJOURS CLIQUABLE */}
        <div ref={dropdownRef} className="relative">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsOpen(!isOpen);
            }}
            className="flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100 hover:from-amber-50 hover:to-amber-100 border-r-2 h-full transition-all duration-200 cursor-pointer select-none"
            aria-label="Changer l'indicatif téléphonique"
            aria-expanded={isOpen}
            data-testid="phone-country-selector"
          >
            <FlagSVG code={selectedCountry.code} />
            <span className="text-sm font-semibold text-gray-700">{selectedCountry.dial}</span>
            <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
          </button>
          
          {/* Dropdown liste des pays groupés par région */}
          {isOpen && (
            <div 
              className="absolute z-[9999] top-full left-0 mt-2 w-80 bg-white border-2 border-gray-200 rounded-2xl shadow-2xl max-h-80 overflow-y-auto"
              style={{ pointerEvents: 'auto' }}
            >
              {groupedCountries.map((group) => (
                <div key={group.key}>
                  {/* Header de région */}
                  <div className={`sticky top-0 px-4 py-2 text-xs font-bold uppercase tracking-wider ${group.color} backdrop-blur-sm border-b border-gray-100 bg-opacity-90`}>
                    {group.name}
                  </div>
                  
                  {/* Pays de la région */}
                  {group.countries.map((country) => (
                    <button
                      key={country.code}
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleCountrySelect(country);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-amber-50 text-left transition-all duration-150 cursor-pointer ${
                        selectedCountry.code === country.code ? 'bg-amber-100 border-l-4 border-amber-500' : 'border-l-4 border-transparent'
                      }`}
                      data-testid={`phone-country-${country.code}`}
                    >
                      <FlagSVG code={country.code} />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-900">{country.name}</p>
                        <p className="text-xs text-gray-500">{country.dial}</p>
                      </div>
                      {selectedCountry.code === country.code && (
                        <span className="text-amber-600 text-lg">✓</span>
                      )}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Champ de saisie */}
        <div className="flex-1 relative">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="tel"
            value={value}
            onChange={handleInputChange}
            placeholder={placeholder || selectedCountry.placeholder}
            className="w-full pl-10 pr-4 py-3 border-0 focus:outline-none focus:ring-0 text-sm font-medium"
            required={required}
          />
        </div>
      </div>
      
      {/* Indicateur visuel du territoire */}
      <div className="flex items-center gap-2 mt-2">
        <FlagSVG code={selectedCountry.code} />
        <span className="text-xs text-gray-500 font-medium">
          {selectedCountry.name} • {selectedCountry.dial}
        </span>
        <span className={`text-xs px-2 py-0.5 rounded-full ${REGIONS[selectedCountry.region]?.color || 'bg-gray-100'}`}>
          {REGIONS[selectedCountry.region]?.name || ''}
        </span>
      </div>
    </div>
  );
}

// Export des données pour utilisation externe
export { PHONE_COUNTRIES, detectCountryFromNumber, FLAGS_SVG, REGIONS };
